import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import {
  Document,
  Dashboard,
  Login,
  SignUp,
  CreateContract,
  Client,
  ContractProposal,
  Employee,
  ContractPayment,
  Profile,
  ReferralProgram,
  ServiceOffers,
  ForgetPassword,
  ResetPassword,
  ChangePassword,
  AllNotifications,
  CreateTimeSheet,
  TimeSheetDashboard,
  Reports,
  MangeClientRole,
  Forbidden,
  PerformanceIndex,
  Leaves,
  WorkingHours,
  HRMS,
  EfilingAdmin,
  Templates,
  BusinessesAdmin,
  ClientTransfer,
  ClientTaxStrategy,
  MyTaxTeam,
  ContactUs,
  SelectAccount,
  MyServices,
  PendingServices,
} from "./pages";
import "./App.css";

import PrivateRoute from "../src/route/privateRoute";
import { AppLoader } from "./components";
import Feedback from "./pages/feedback";
import ClientDetails from "./pages/client_details";
import Messages from "./pages/messages";
import {
  hasPersistedAuth,
  isSessionExpired,
} from "./utils/sessionAuth";
import { getUserRole, getRolePath } from "./utils/common";
import {
  hydrateAuthSession,
  startAuthTamperGuard,
} from "./utils/authSession";

const ProtectedSelectAccount = ({ children }) => {
  const [ready, setReady] = React.useState(false);
  const [userRole, setUserRole] = React.useState(() => getUserRole());
  const [isAuthed, setIsAuthed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    startAuthTamperGuard();

    (async () => {
      if (!hasPersistedAuth()) {
        if (!cancelled) {
          setIsAuthed(false);
          setReady(true);
        }
        return;
      }

      try {
        const session = await hydrateAuthSession({ force: true });
        if (cancelled) return;
        const role = session?.role || getUserRole();
        setUserRole(role);
        setIsAuthed(Boolean(session?.userId) && !isSessionExpired({ role }));
      } catch (_) {
        if (!cancelled) setIsAuthed(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return null;

  if (!isAuthed) {
    return <Navigate to="/login" replace />;
  }
  if (userRole !== "client") {
    return <Navigate to={getRolePath("/dashboard")} replace />;
  }
  return children;
};

const App = () => {
  return (
    <>
      <AppLoader />
      <Router>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forget_password" element={<ForgetPassword />} />
          <Route path="/forbidden" element={<Forbidden />} />
          {/* <Route path="/reset_password/:id" element={<ResetPassword />} /> */}
          <Route path="/reset_password/" element={<ResetPassword />} />
          <Route
            path="/client/select-account"
            element={
              <ProtectedSelectAccount>
                <SelectAccount />
              </ProtectedSelectAccount>
            }
          />

          <Route path="/finlo/*" element={<PrivateRoute />}>
            <Route path=":role/change_password" element={<ChangePassword />} />
            <Route path=":role/dashboard" element={<Dashboard />} />
            <Route path=":role/documents" element={<Document />} />
            <Route path=":role/employees" element={<Employee />} />
            <Route path=":role/client" element={<Client />} />
            <Route
              path=":role/new_client_requests"
              element={<Client requestMode />}
            />
            <Route path=":role/client_details/:id" element={<ClientDetails />} />
            <Route path=":role/messages" element={<Messages />} />
            <Route path=":role/tax_strategy" element={<ClientTaxStrategy />} />
            <Route path=":role/my_tax_team" element={<MyTaxTeam />} />
            <Route path=":role/my_services" element={<MyServices />} />
            <Route path=":role/pending_services" element={<PendingServices />} />
            <Route path=":role/reports" element={<Reports />} />
            <Route path=":role/feedback" element={<Feedback />} />
            <Route path=":role/contract_proposal" element={<ContractProposal />} />
            <Route path=":role/payment" element={<ContractPayment />} />
            <Route path=":role/profile" element={<Profile />} />
            <Route path=":role/referral" element={<ReferralProgram />} />
            <Route path=":role/invoice_contract" element={<CreateContract />} />
            <Route path=":role/service_offers" element={<ServiceOffers />} />
            <Route path=":role/our_services" element={<ServiceOffers />} />
            <Route path=":role/contact_us" element={<ContactUs />} />
            <Route path=":role/create_timesheet" element={<CreateTimeSheet />} />
            <Route path=":role/timesheet_dashboard" element={<TimeSheetDashboard />} />
            <Route path=":role/notifications" element={<AllNotifications />} />
            <Route path=":role/manage_client_role" element={<MangeClientRole />} />
            <Route path=":role/client_transfer" element={<ClientTransfer />} />
            <Route path=":role/performance_index" element={<PerformanceIndex />} />
            <Route path=":role/leaves" element={<Leaves />} />
            <Route path=":role/working_hours" element={<WorkingHours />} />
            <Route path=":role/efiling" element={<EfilingAdmin />} />
            <Route path=":role/templates" element={<Templates />} />
            <Route path=":role/businesses" element={<BusinessesAdmin />} />
            <Route path=":role/hrms" element={<HRMS />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </Router>
    </>
  );
};

export default App;
